import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useViewers } from '../hooks/useViewers';
import {
  viewerLinkTarget,
  type ViewerOption,
  type ViewerTarget,
} from '../utils/viewers';

const PANEL_WIDTH = 240;
const EDGE_GAP = 8;

interface OpenInMenuProps {
  options: ViewerOption[];
  onSetDefault: (viewerId: string) => void;
  subject?: string;
  className?: string;
}

/** Portalled so grid cells, which clip overflow, can host the trigger. */
export function OpenInMenu({
  options,
  onSetDefault,
  subject,
  className = '',
}: OpenInMenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    maxHeight: 0,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  }, []);

  const toggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const maxLeft = window.innerWidth - PANEL_WIDTH - EDGE_GAP;
      setPosition({
        top: rect.bottom + 4,
        left: Math.max(EDGE_GAP, Math.min(rect.right - PANEL_WIDTH, maxLeft)),
        maxHeight: window.innerHeight - rect.bottom - 4 - EDGE_GAP,
      });
    }
    setOpen(true);
  };

  useLayoutEffect(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    const height = panelRef.current?.scrollHeight;
    if (!open || !rect || height === undefined) return;
    const below = window.innerHeight - rect.bottom - 4 - EDGE_GAP;
    const above = rect.top - 4 - EDGE_GAP;
    if (height <= below || below >= above) return;
    setPosition(p => ({
      ...p,
      top: rect.top - 4 - Math.min(height, above),
      maxHeight: above,
    }));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>('a, button')?.focus();
    const closeIfOutside = (event: Event) => {
      const target = event.target as Node;
      if (
        panelRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }
      close(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close(true);
        return;
      }
      const panel = panelRef.current;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (!panel?.contains(document.activeElement)) return;
      const items = [...panel.querySelectorAll<HTMLElement>('a, button')];
      event.preventDefault();
      const index = items.indexOf(document.activeElement as HTMLElement);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      items[(index + step + items.length) % items.length].focus();
    };
    const onScroll = (event: Event) => {
      if (panelRef.current?.contains(event.target as Node)) return;
      close(false);
    };
    const onResize = () => close(false);
    document.addEventListener('focusin', closeIfOutside);
    document.addEventListener('mousedown', closeIfOutside);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('focusin', closeIfOutside);
      document.removeEventListener('mousedown', closeIfOutside);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  if (options.filter(option => option.url !== null).length < 2) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={subject ? `Open ${subject} in…` : 'Open in…'}
        title="Open in another viewer"
        className={`row-action row-action-accent inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap ${className}`}
      >
        Open in
        <svg
          width="10"
          height="10"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m4 6 4 4 4-4" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="group"
            aria-label="Viewers"
            className="pane fixed z-50 overflow-y-auto py-1 shadow-lg"
            style={{
              top: position.top,
              left: position.left,
              maxHeight: position.maxHeight,
              width: PANEL_WIDTH,
            }}
          >
            <ul>
              {options.map(({ viewer, url, isDefault }) => (
                <li
                  key={viewer.id}
                  className="flex items-center gap-2 px-1 hover:bg-hover"
                >
                  {url === null ? (
                    <span
                      aria-disabled="true"
                      className="min-w-0 flex-1 truncate px-2 py-1.5 text-sm text-muted"
                    >
                      {viewer.name}
                      <span className="ml-1.5 text-[11px]">Not supported</span>
                    </span>
                  ) : (
                    <a
                      href={url}
                      {...viewerLinkTarget(url)}
                      onClick={() => close(false)}
                      className="min-w-0 flex-1 truncate rounded px-2 py-1.5 text-sm text-ink no-underline"
                    >
                      {viewer.name}
                    </a>
                  )}
                  {isDefault ? (
                    <span className="shrink-0 pr-2 text-[11px] text-muted">
                      Default
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.currentTarget.parentElement
                          ?.querySelector<HTMLElement>('a, button')
                          ?.focus();
                        onSetDefault(viewer.id);
                      }}
                      aria-label={`Set ${viewer.name} as default`}
                      title={`Open with ${viewer.name} by default`}
                      className="row-action shrink-0 pr-2 text-[11px]"
                    >
                      Set default
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </>
  );
}

export function OpenInViewerMenu({
  target,
  subject,
  className,
}: {
  target: ViewerTarget;
  subject?: string;
  className?: string;
}) {
  const { options, setDefaultViewerId } = useViewers();
  return (
    <OpenInMenu
      options={options(target)}
      onSetDefault={setDefaultViewerId}
      subject={subject}
      className={className}
    />
  );
}
